import { useEffect, useState } from 'react';
import { ToastProvider } from '../components/Toast';
import { ToolsLoginRedirect } from '../components/ToolsLoginRedirect';
import { redirectToToolsLogout } from '../lib/tools-login';
import { Spinner } from '../components/ui/Spinner';
import { api, apiTryGet } from '../lib/api';
import { type AuthedUser, deriveAuthedUser, type Me } from '../lib/auth-types';
import { TransferPage } from '../pages/TransferPage';
import { TransferShell } from './TransferShell';

/**
 * TransferApp — Auth-Shell des schlanken Transfer-Clients (Phase 2).
 *
 * Analog `App.tsx`, aber getrimmt: EAGER Login/TransferPage (kein Code-Splitting/
 * Suspense/ChunkErrorBoundary — der Bundle ist single-purpose), KEIN Sidebar/
 * Layout, kein OnboardingTour, kein Preload. Serviert wird nur die Transfer-
 * Domäne; Auth läuft gegen das geteilte `users`-Verzeichnis (Option B: eigenes
 * Cookie/Secret entsteht durch den getrennten Prozess + Env-File, nicht hier).
 */
type AuthState =
  | { status: 'checking' }
  | { status: 'unauthed' }
  | { status: 'authed'; user: AuthedUser };

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream">
      <Spinner size="lg" className="text-brand-green-700" />
      <span className="sr-only">Lädt…</span>
    </div>
  );
}

export function TransferApp() {
  const [auth, setAuth] = useState<AuthState>({ status: 'checking' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await apiTryGet<Me>('/api/auth/me');
      if (cancelled) return;
      setAuth(
        me ? { status: 'authed', user: deriveAuthedUser(me) } : { status: 'unauthed' },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth.status === 'checking') return <LoadingScreen />;

  // P1c PR-C (Must 10): kein eigener Transfer-Login mehr — tools.greenforce.com
  // ist der einzige IdP. `app='transfer'` als Literal (Parität zu App.tsx).
  if (auth.status === 'unauthed') {
    return <ToolsLoginRedirect app="transfer" />;
  }

  // P1c PR-C: „Abmelden" ist ab jetzt ein GLOBALER Logout. Nur das lokale Cookie
  // zu löschen würde nichts bewirken — der unauthed-Branch schickt den User nach
  // tools, wo die IdP-Session noch lebt und laut PR-A sofort einen Auto-Handoff
  // zurück mintet. Deshalb: lokal abmelden, dann nach `tools/logout`, wo die
  // Session an der Wurzel stirbt. KEIN `setAuth('unauthed')` mehr — das würde den
  // Login-Redirect (mit `app`/`next`) auslösen und damit genau den Rundflug, den
  // wir verhindern wollen.
  const handleLogout = async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch (err) {
      console.error('[auth] logout failed:', err);
    }
    redirectToToolsLogout();
  };

  // Forced-Change-Gate (P1c PR-C, Must 11): Parität zur DAM-App — der Wechsel
  // passiert auf tools, `change-password` existiert auf Transfer nicht mehr.
  // Die Subdomain bleibt damit weiterhin kein Bypass für Temp-Passwörter.
  if (auth.user.mustChangePassword) {
    return <ToolsLoginRedirect app="transfer" />;
  }

  return (
    <ToastProvider>
      <TransferShell user={auth.user} onLogout={handleLogout}>
        <TransferPage />
      </TransferShell>
    </ToastProvider>
  );
}
